/** Warehouse map layer — Warehouse Master only. Never invent lat/lon or serving radii. */
import type { Warehouse, WarehouseStatus, WarehouseType } from '../../api/warehouseMaster';
import { normalizeIndiaState } from './normalizeState';
import type { IndiaStateCode } from './types';

export type WarehouseMapRow = {
  id: string;
  code: string;
  name: string;
  city: string;
  stateLabel: string;
  stateCode: IndiaStateCode | null;
  region: string;
  warehouseType: WarehouseType | string;
  status: WarehouseStatus | string;
  lastVerified: string | null;
  latitude: number | null;
  longitude: number | null;
  /** True only when master supplies both coordinates (no invent). */
  hasCoords: boolean;
};

export type WarehouseMapPin = WarehouseMapRow & {
  latitude: number;
  longitude: number;
  hasCoords: true;
};

/** Rough mainland India bbox — drop coords outside only for plotting; keep row in lists. */
function inIndiaPlotBbox(lat: number, lon: number): boolean {
  return lat >= 6 && lat <= 38 && lon >= 66 && lon <= 98;
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function warehouseToMapRow(w: Warehouse): WarehouseMapRow {
  const lat = numOrNull(w.latitude);
  const lon = numOrNull(w.longitude);
  const hasCoords =
    lat != null && lon != null && inIndiaPlotBbox(lat, lon);
  return {
    id: w.id,
    code: w.warehouse_code,
    name: w.warehouse_name || w.warehouse_code,
    city: (w.city ?? '').trim(),
    stateLabel: (w.state ?? '').trim(),
    stateCode: normalizeIndiaState(w.state),
    region: (w.region ?? '').trim(),
    warehouseType: w.warehouse_type || 'unknown',
    status: w.status || 'unknown',
    lastVerified: w.last_verified ?? null,
    latitude: lat,
    longitude: lon,
    hasCoords,
  };
}

export function buildWarehouseMapLayer(items: Warehouse[]): {
  warehouses: WarehouseMapRow[];
  pins: WarehouseMapPin[];
  missingCoords: number;
  byState: Record<IndiaStateCode, WarehouseMapRow[]>;
  coverageStateCodes: IndiaStateCode[];
} {
  const warehouses = items.map(warehouseToMapRow);
  const pins = warehouses.filter((r): r is WarehouseMapPin => r.hasCoords);
  const byState = {} as Record<IndiaStateCode, WarehouseMapRow[]>;
  for (const r of warehouses) {
    if (!r.stateCode) continue;
    if (!byState[r.stateCode]) byState[r.stateCode] = [];
    byState[r.stateCode].push(r);
  }
  const coverageStateCodes = (Object.keys(byState) as IndiaStateCode[]).sort();
  return {
    warehouses,
    pins,
    missingCoords: warehouses.length - pins.length,
    byState,
    coverageStateCodes,
  };
}

export function warehousesForState(
  rows: WarehouseMapRow[],
  code: IndiaStateCode,
): WarehouseMapRow[] {
  return rows.filter((r) => r.stateCode === code);
}
